import _ from 'lodash';
import React, { useContext, useMemo } from 'react';
import { UserContext } from '@/contexts/UserContext';
import {
    DEFAULT_DELIVERY_TIMEFRAME,
    DEFAULT_SERVICE_TIME,
    DEFAULT_SERVICE_TIME_C,
    DEFAULT_SERVICE_TIME_FDS,
    DEFAULT_SERVICE_TIME_INCREMENT,
    DEFAULT_SERVICE_TIME_ROC,
    DEFAULT_SERVICE_TIME_WG,
    DEFAULT_TIMEFRAME_BUFFER,
} from '@/constants/deliveryTimeframes';
import { defaultSettings as defaultThresholdSettings } from '@/components/Account/MilestoneThresholds';
import { teammateRoles } from '@/components/Account/Teammates';
import { ASSET_HEAVY } from '@/constants/featureFlags';
import { mergeTags } from '@/utilities/mergeTags';
import { buildPartnerCarrierMap } from '@/utilities/carrierAlias';

// carrier_partnerships is ordered by shipper name, which is null on contractor rows.
const subcontractedPartners = (client) =>
    (client?.carrier_partnerships || [])
        .filter((p) => p?.contractor_id && p?.contractor)
        .sort((l, r) =>
            String(l.contractor?.business_name || '').localeCompare(String(r.contractor?.business_name || ''))
        );

export const useClientUser = () => {
    const { user, realUser, simOrgId, clientId, client, teammate, org_tags } = useContext(UserContext);

    return useMemo(() => {
        const userDispatchSettings = (client?.dispatch_settings || []).find((ds) => !ds.partner_client_id);
        const dispatchSettingsByClientId = (client?.dispatch_settings || []).reduce((acc, ds) => {
            acc[ds.partner_client_id || 'DEFAULT'] = ds;
            return acc;
        }, {});

        // Client IDs whose tariffs this user may edit: their own account plus any CLIENT-scoped
        // TARIFF WRITE grants. Centralized here because ~a dozen tariff queries build this same list.
        const writableTariffClientIds = [
            ...(client?.client_id ? [client.client_id] : []),
            ...(client?.permissions || [])
                .filter((p) => p.permission_type === 'TARIFF' && p.entity_type === 'CLIENT' && p.scope === 'WRITE')
                .map((p) => p.entity_id),
        ];

        return {
            isOnwardEngineer: !!user?.roles?.['ONWARD_ENGINEER'],
            isOnwardAdmin: !!user?.roles?.['ONWARD_ADMIN'] && !client?.client_id,
            isCorporateAdmin: !!user?.roles?.['ADMIN'] && !client?.client_id,
            isCarrierAdmin:
                !!client?.user?.roles?.['CARRIER'] &&
                !!client?.user?.circles?.[ASSET_HEAVY] &&
                (!teammate?.teammate_id || ['admin', 'invoicing'].some((role) => teammate?.roles?.[role])),
            isImposter: !!user?.roles?.['ONWARD_ADMIN'] && client?.client_id,
            // An Onward admin simulating an org admin. useOrgSimulation only sets simOrgId for an Onward admin. `user`
            // then carries roles.ADMIN, so isCorporateAdmin is true and isOnwardAdmin / isImposter are false.
            isOrgImposter: !!simOrgId,
            // An admin acting as an account. Keyed on the signed-in row: a shipper's own clientId doesn't count.
            isActingAsAccount: !!clientId && !!(realUser?.roles?.['ADMIN'] || realUser?.roles?.['ONWARD_ADMIN']),
            organization: client?.user?.organization || user?.organization,
            organization_id: client?.user?.organization_id || user?.organization_id,
            roles: client?.user?.roles || user?.roles,
            circles: client?.user?.circles || user?.circles,
            isSsoUser: !!user?.sso_tenant,
            ...(client?.user?.roles?.['SHIPPER']
                ? { accountType: 'shipper', userType: 'shipper' }
                : client?.user?.roles?.['CARRIER'] || client?.user?.roles?.['CONTRACTOR']
                ? { accountType: 'carrier', userType: 'carrier' }
                : {}),
            user_id: client?.client_id,
            client_id: client?.client_id,
            username: client?.username,
            email: client?.email,
            approved: !!client?.approved,
            test_acc: !!client?.test_acc,
            permissions: (client?.permissions || []).reduce((acc, permission) => {
                acc[permission.permission_type] = [...(acc[permission.permission_type] || []), permission];
                return acc;
            }, {}),
            writableTariffClientIds,
            businessName: client?.business_name,
            businessPhone: client?.business_phone,
            businessState: client?.business_state || client?.billing_state,
            businessZip: client?.business_zip || client?.billing_zip,
            cc_valid: client?.cc_valid,
            payment_type: client?.payment_type,
            stripe_user_id: client?.stripe_user_id,
            stripe_payment_method: client?.stripe_payment_method,
            pricing_overrides: client?.pricing_overrides || [],
            teammates: client?.user?.teammatesByLeaderId || [],
            drivers: [...(client?.user?.teammatesByLeaderId || [])]
                .filter((tm) => tm.roles?.driver || tm.roles?.admin)
                .sort((a, b) => a.username.localeCompare(b.username)),
            helpers: [...(client?.user?.teammatesByLeaderId || [])]
                .filter((tm) => tm.roles?.helper || tm.roles?.admin)
                .sort((a, b) => a.username.localeCompare(b.username)),
            auto_assigned_carrier: client?.auto_assigned_carrier,
            resources: client?.available_resources || [],
            locations: (client?.locations || []).filter((l) => !l.is_customer_location),
            customer_locations: (client?.locations || []).filter((l) => l.is_customer_location),
            // Consumers read partner.shipper unguarded; carrierAdminPayableScope reads the raw
            // client.carrier_partnerships and still wants the contractor rows.
            shipping_partners: (client?.carrier_partnerships || []).filter((p) => p?.shipper_id),
            partner_carriers: client?.shipper_partnerships || [],
            // partner_carriers indexed by carrier_id, so a lookup by id doesn't need a `.find()`.
            // Derived from shipper_partnerships, so it is empty for a carrier — which is what
            // makes carrierDisplayName's alias shipper-only without a role check.
            partnerCarriersById: buildPartnerCarrierMap(client?.shipper_partnerships),
            partner_contractors: subcontractedPartners(client),
            shipper_integrations: client?.shipper_integrations || [],
            dispatch_integration_type: client?.dispatch_integration_type,
            dispatch_integration_meta: client?.dispatch_integration_meta,
            service_levels: client?.service_levels || [],
            tags: mergeTags(client?.tags, org_tags).sort((a, b) => a.tag.localeCompare(b.tag)),
            customer_messages: client?.customer_messages || {},
            survey_questions: client?.survey_questions || [],
            settings: client?.settings || {},
            savedShapes: client?.map_shapes,
            default_end_location: client?.locations?.find((l) => l.default_end_location),
            commingled_internal_mandatory: client?.commingled_internal_mandatory,
            qbo_tags: client?.qbo_tags || [],
            documents: client?.documents || [],
            auto_assigned_admin_id: client?.auto_assigned_admin_id || null,
            preferences_service_time: userDispatchSettings?.preferences_service_time || DEFAULT_SERVICE_TIME,
            preferences_use_service_default_as_minimum:
                userDispatchSettings?.preferences_use_service_default_as_minimum || false,
            preferences_require_staging_verification:
                userDispatchSettings?.preferences_require_staging_verification || false,
            preferences_service_time_incr:
                userDispatchSettings?.preferences_service_time_incr || DEFAULT_SERVICE_TIME_INCREMENT,
            preferences_service_time_curbside:
                userDispatchSettings?.preferences_service_time_curbside || DEFAULT_SERVICE_TIME_C,
            preferences_service_time_first_dry_space:
                userDispatchSettings?.preferences_service_time_first_dry_space || DEFAULT_SERVICE_TIME_FDS,
            preferences_service_time_room_of_choice:
                userDispatchSettings?.preferences_service_time_room_of_choice || DEFAULT_SERVICE_TIME_ROC,
            preferences_service_time_white_glove:
                userDispatchSettings?.preferences_service_time_white_glove || DEFAULT_SERVICE_TIME_WG,
            preferences_delivery_timeframe:
                userDispatchSettings?.preferences_delivery_timeframe || DEFAULT_DELIVERY_TIMEFRAME,
            preferences_timeframe_buffer:
                userDispatchSettings?.preferences_timeframe_buffer || DEFAULT_TIMEFRAME_BUFFER,
            preferences_timeframe_buffer_apply: userDispatchSettings?.preferences_timeframe_buffer_apply || false,
            preferences_timeframe_round: userDispatchSettings?.preferences_timeframe_round || false,
            preferences_timeframe_round_minute: userDispatchSettings?.preferences_timeframe_round_minute ?? null,
            preferences_route_time_limit: client?.preferences_route_time_limit || 0,
            preferences_route_eod_limit: client?.preferences_route_eod_limit || 0,
            preferences_delivery_window_floor: userDispatchSettings?.preferences_delivery_window_floor || null,
            preferences_delivery_window_ceiling: userDispatchSettings?.preferences_delivery_window_ceiling || null,
            preferences_default_start_time: userDispatchSettings?.preferences_default_start_time || null,
            preferences_route_eod_limit_next_day: client?.preferences_route_eod_limit_next_day || false,
            preferences_start_estimate: client?.preferences_start_estimate || false,
            preferences_end_estimate: client?.preferences_end_estimate || false,
            preferences_warehouse_return_estimate: client?.preferences_warehouse_return_estimate || false,
            preferences_next_day_returns: client?.preferences_next_day_returns || false,
            preferences_bypass_optimize: client?.preferences_bypass_optimize || false,
            special_instructions_settings: client?.special_instructions_settings || {},
            invoiceAccess: true,
            onward_admin_notes: client?.onward_admin_notes || null,
            ...(teammate?.teammate_id
                ? {
                      userType: 'teammate',
                      teammateRoles: teammate.roles?.admin
                          ? Object.fromEntries(Object.keys(teammateRoles).map((role) => [role, true]))
                          : teammate.roles,
                      invoiceAccess: teammate?.roles?.admin || teammate?.roles?.invoicing,
                  }
                : {}),
            white_label_img: client?.white_label_img || null,
            white_label_color: client?.white_label_color || null,
            milestone_threshold_settings: client?.milestone_threshold_settings || defaultThresholdSettings,
            dispatch_settings: dispatchSettingsByClientId || {},
        };
    }, [user, realUser, simOrgId, clientId, client, teammate, org_tags]);
};
